import React, { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import dayjs from 'dayjs';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import Yup from '../../utils/yup';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useUsers from '../../context/UsersContext';
import { FormHeaderUI, FormButtonPanelUI, DeleteDialogUI, ButtonUI, FileUploadButtonUI, DialogUI } from '../Interface';
import employeeAttendenceService from '../../service/employeeAttendence.service';
import EmployeeAttendanceFields from './EmployeeAttendanceFields';
import EmployeeAttendanceTabs from './EmployeeAttendanceTabs';
import { EmployeeAttendanceMode } from '../../config/employeeAttendance';
import { EmployeeAttendanceSimpleMode } from '../../utils/employeeAttendance';
import { Box } from '@mui/material';
import useParam from '../../context/ParamContext';
import taskListService from '../../service/taskList.service';
import { generateAutocompleteItems } from '../../utils/helper';
import dynamicFormService from '../../service/dynamicForm.service';
import Worksheet from '../Client/Odor/Worksheet/Worksheet';

export const EmployeeAttendancePage = ({ employeAttendanceState, dialog, mode = EmployeeAttendanceMode.MAIN }) => {
  const {
    setRefetchTable,
    viewField,
    setViewField,
    selectedValues,
    setSelectedValues,
    operatorsDisabled,
    setOperatorsDisabled,
    canEdit,
    autocompleteData,
    autocompleteOptions,
    setAutoCompleteOptions,
    editButtonVisible,
    setEditButtonvisible,
    setRowSelectionModel,
    selectedRow,
    setSelectedRow,
    defaultValues,
    setDefaultValues,
    isLoading,
    refetchTable,
  } = employeAttendanceState;
  const { params } = useParam();
  const { user } = useUsers();
  const [initValues, setInitValues] = useState({});
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [addField, setAddField] = useState(true);
  const [defaultSelectedValues, setDefaultSelectedValues] = useState({});
  const [myReports, setMyReports] = useState([]);
  const [worksheetLaunch, setWorksheetLaunch] = useState(null);

  const defaultEmployeeLine = {
    id: null,
    isNew: true,
    employeeId: null,
    rating: null,
    fromTime: null,
    toTime: null,
    timeSpent: null,
    absence: false,
    description: '',
    headCount: '',
    companyId: null,
    createdBy: user.userId,
    taskListId: null,
    classificationId: null,
  };

  const def = {
    date: dayjs(new Date()),
    projectId: null,
    simpleTaskId: null,
    statusId: null,
    employeeAttendanceLine: [],
    description: '',
    employeeAttendanceAttachment: [],
    simpleTaskName: '',
    projectOrderNumber: '',
    projectCompanyLocationName: '',
  };

  useEffect(() => {
    setDefaultValues(def);
  }, []);

  useEffect(() => {
    if (Object.keys(dialog?.data || {}).length > 0 && !isLoading) {
      setDefaultValues((prev) => ({
        ...prev,
        ...dialog?.data,
      }));
    }
  }, [dialog?.data, isLoading]);

  useEffect(() => {
    if (Object.keys(autocompleteData).length > 0) {
      setDefaultValues((prev) => {
        return {
          ...prev,
          statusId: autocompleteData?.status?.defaultValue ?? null,
        };
      });

      setDefaultSelectedValues((prev) => {
        return {
          ...prev,
          status: autocompleteData?.status?.defItem,
        };
      });
    }
  }, [autocompleteData]);

  useEffect(() => {
    setSelectedValues(defaultSelectedValues);
  }, [defaultSelectedValues]);

  useEffect(() => {
    if (EmployeeAttendanceSimpleMode(mode) && !isLoading) {
      getMyReports();
      setRefetchTable(false);
    }
  }, [mode, isLoading, refetchTable]);

  useEffect(() => {
    if (Object.keys(selectedValues).length > 0) {
      if (!_.isEqual(defaultSelectedValues, selectedValues)) {
        valuesToForm();
        setOperatorsDisabled(false);
      } else {
        setOperatorsDisabled(true);
        setInitValues(defaultValues);
      }
    }
  }, [selectedValues]);

  const getMyReports = () => {
    employeeAttendenceService.getMyReports().then((data) => {
      setMyReports(data);
    });
  };

  const updateBatchedStatuses = (values) => {
    const updData = {
      statusId: values.statusId,
      selectedRows: selectedRow.id,
    };
    employeeAttendenceService.updateBatchedStatus(updData).then(() => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setSelectedRow({
        id: [],
      });
    });
  };

  const valuesToForm = () => {
    const {
      date,
      projectId,
      statusId,
      employeeAttendanceLine,
      description,
      employeeAttendanceAttachment,
      simpleTaskId,
    } = selectedValues;

    setInitValues({
      date: dayjs(date),
      projectId,
      statusId,
      projectOrderNumber: selectedValues.project?.orderNumber || '',
      projectCompanyLocationName: selectedValues.project?.companyLocation?.name || '',
      simpleTaskName: selectedValues.simpleTask?.simpleTaskName || '',
      description: description ?? '',
      employeeAttendanceAttachment,
      simpleTaskId,
      employeeAttendanceLine: employeeAttendanceLine?.map((au) => ({
        id: au.id,
        employeeId: au.employeeId,
        rating: au.rating,
        fromTime: au.fromTime,
        toTime: au.toTime,
        attendanceId: au.id,
        timeSpent: au.timeSpent,
        createdBy: au.createdBy,
        absence: au.absence,
        description: au.description,
        companyId: au.companyId,
        headCount: au.headCount,
        taskListId: au.taskListId,
        classificationId: au.classificationId,
      })),
    });
  };

  const contractorsManagementEnabled = params.EMPLOYEE_ATTENDANCE_CONTRACTORS_MANAGEMENT === 'Y';
  const simpleTaskFieldShowed = params.SHOW_EMPLOYEE_ATTENDANCE_SIMPLETASK_FIELD === 'Y';
  const isSimpleTaskOrProjectMode = simpleTaskFieldShowed && mode === EmployeeAttendanceMode.DIALOG;

  const validationSchema = Yup.object()
    .shape({
      date: Yup.string().required(),
      projectId: Yup.string().when([], {
        is: () => !isSimpleTaskOrProjectMode,
        then: (schema) => schema.required(),
        otherwise: (schema) => schema.nullable(),
      }),
      simpleTaskId: Yup.string().nullable(),
      statusId: Yup.string().required(),
      projectOrderNumber: Yup.string().nullable(),
      projectCompanyLocationName: Yup.string().nullable(),
      description: Yup.string().nullable(),
      employeeAttendanceLine: Yup.array().of(
        Yup.object()
          .shape({
            employeeId: Yup.string().when([], {
              is: () => !contractorsManagementEnabled,
              then: (schema) => schema.required(),
              otherwise: (schema) => schema.nullable(),
            }),
            companyId: Yup.string().nullable(),
            timeSpent: Yup.number().required().positive().max(24),
            rating: Yup.number().nullable().min(0).max(9.99),
            fromTime: Yup.string().required(),
            toTime: Yup.string().required(),
            absence: Yup.boolean(),
            description: Yup.string().nullable(),
            headCount: Yup.number().when('companyId', {
              is: (companyId) => !!companyId,
              then: (schema) => schema.required().positive(),
              otherwise: (schema) => schema.nullable().positive(),
            }),
            taskListId: Yup.string().nullable(),
            classificationId: Yup.string().nullable(),
          })
          .test('employee-or-company-required', 'Dolgozó vagy cég megadása kötelező!', function (value) {
            if (!contractorsManagementEnabled) return true;

            const { employeeId, companyId } = value || {};

            if (!employeeId && !companyId) {
              return this.createError({
                path: `${this.path}.employeeId`,
                message: 'Dolgozó vagy cég megadása kötelező!',
              });
            }

            return true;
          })
      ),
    })
    .test('simpleTask-or-project-required', 'Feladat vagy projekt megadása kötelező!', function (value) {
      if (!isSimpleTaskOrProjectMode) return true;

      const { simpleTaskId, projectId } = value || {};

      if (!simpleTaskId && !projectId) {
        return this.createError({
          path: `simpleTaskId`,
          message: 'Feladat vagy projekt megléte kötelező!',
        });
      }

      return true;
    });

  const insertObject = (values, resetForm) => {
    const insertObject = {
      ...values,
      date: dayjs(values.date).startOf('day').add(12, 'hours').toISOString(),
      userId: user.userId,
      createdBy: user.userId,
      employeeAttendanceLine: values.employeeAttendanceLine.map((line) => ({
        ...line,
        headCount: parseInt(line.headCount),
      })),
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    if (insertObject.employeeAttendanceAttachment) {
      for (const file of insertObject.employeeAttendanceAttachment) {
        formData.append('files', file);
      }
    }

    employeeAttendenceService
      .createRow(formData)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        if (mode === EmployeeAttendanceMode.MAIN) {
          setRefetchTable(true);
          setAddField(true);
          setViewField(true);
          setSelectedValues(data);
          setRowSelectionModel(data.id);
        } else if (mode === EmployeeAttendanceMode.PHONE) {
          resetForm();
          if (data.projectId) {
            dynamicFormService
              .prepareAttendanceWorksheet(data.id)
              .then((launch) => setWorksheetLaunch(launch))
              .catch(() =>
                addNotification({
                  content: 'A lejelentés elmentve, de a munkalapot nem sikerült megnyitni.',
                  type: NotificationType.WARNING,
                })
              );
          }
        } else if (mode === EmployeeAttendanceMode.DIALOG) {
          resetForm();
          dialog.handleClose();
        }
        setRefetchTable(true);
      })

      .finally(() => {
        //@TODO loader
      });
  };

  const updateObject = (values) => {
    const updData = {
      ...values,
      date: dayjs(values.date).startOf('day').add(12, 'hours').toISOString(),
      updatedBy: user.userId,
      employeeAttendanceLine: values.employeeAttendanceLine.map((line) => ({
        ...line,
        headCount: parseInt(line.headCount),
      })),
    };

    updData.existingFiles = updData.employeeAttendanceAttachment?.map((f) => f.name);

    const formData = new FormData();
    formData.append('data', JSON.stringify(updData));

    if (updData.employeeAttendanceAttachment) {
      for (const file of updData.employeeAttendanceAttachment) {
        if (file instanceof File) {
          formData.append('files', file);
        }
      }
    }

    employeeAttendenceService.updateRow(formData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres módosítás!',
        type: NotificationType.SUCCESS,
      });
      setRefetchTable(true);
      setEditButtonvisible(true);
      setViewField(true);
    });
  };

  const handleSubmit = (values, { resetForm }) => {
    if (!editButtonVisible) {
      updateObject(values);
    } else if (!addField || EmployeeAttendanceSimpleMode(mode)) {
      insertObject(values, resetForm);
    }
  };

  const handleRemoveElement = async () => {
    const deleteData = {
      deletedBy: user.userId,
    };
    employeeAttendenceService.deleteRow(deleteData, selectedValues.id).then(() => {
      addNotification({
        content: 'Sikeres törlés!',
        type: NotificationType.WARNING,
      });
      setRefetchTable(true);
      setShowDeleteDialog(false);
      setSelectedValues(defaultSelectedValues);
    });
  };

  const handleTemplate = () => {
    employeeAttendenceService.getExcelTemplate().then(() => {
      addNotification({
        content: 'A letöltés elindult!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const handleImport = (files) => {
    employeeAttendenceService.importExcel(files, user.userId).then(() => {
      setRefetchTable(true);
      addNotification({
        content: 'Az import sikeres!',
        type: NotificationType.SUCCESS,
      });
    });
  };

  const getTaskListByProjectId = (projectId) => {
    if (params.SHOW_EMPLOYEE_ATTENDANCE_TASKLIST_FIELD !== 'Y') return;
    if (!projectId) {
      setAutoCompleteOptions((prev) => ({
        ...prev,
        taskList: [],
      }));
      return;
    }

    taskListService.getTasklistsByLineId(projectId).then((data) => {
      const options = generateAutocompleteItems(data, 'name', 'id');
      setAutoCompleteOptions((prev) => ({
        ...prev,
        taskList: options,
      }));
    });
  };

  return (
    <>
      <div className="h-full overflow-auto custom-scrollbar">
        {mode === EmployeeAttendanceMode.MAIN && (
          <FormHeaderUI
            addClick={() => {
              setSelectedValues(defaultSelectedValues);
              setRowSelectionModel([]);
              setInitValues(defaultValues);
              setViewField(false);
              setAddField(false);
            }}
            addDisabled={!viewField || !canEdit}
            modClick={() => {
              setViewField(false);
              setEditButtonvisible(false);
            }}
            modDisabled={!viewField || operatorsDisabled || !canEdit}
            deleteClick={() => {
              setShowDeleteDialog(true);
            }}
            deleteDisabled={!viewField || operatorsDisabled || !canEdit}
            copyClick={() => {
              setViewField(false);
              setAddField(false);
            }}
            copyDisabled={!viewField || operatorsDisabled || !canEdit}
          />
        )}

        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, resetForm, setFieldValue }) => {
            const handleUploadFile = (files) => {
              const filesWithId = files.map((file) => {
                file.id = uuidv4();
                return file;
              });

              const existingAttachments = Array.isArray(values.employeeAttendanceAttachment)
                ? values.employeeAttendanceAttachment
                : [];

              setFieldValue('employeeAttendanceAttachment', [...existingAttachments, ...filesWithId]);
            };

            return (
              <Form>
                <EmployeeAttendanceFields
                  viewField={viewField && !EmployeeAttendanceSimpleMode(mode)}
                  selectedValues={selectedValues}
                  selectedRow={selectedRow}
                  autocompleteOptions={autocompleteOptions}
                  mode={mode}
                  autocompleteData={autocompleteData}
                  getTaskListByProjectId={getTaskListByProjectId}
                />
                <EmployeeAttendanceTabs
                  setRefetchTable={setRefetchTable}
                  selectedValues={selectedValues}
                  user={user}
                  disabled={viewField && !EmployeeAttendanceSimpleMode(mode)}
                  defaultField={defaultEmployeeLine}
                  autocompleteOptions={autocompleteOptions}
                  handleTemplate={handleTemplate}
                  handleImport={handleImport}
                  mode={mode}
                  handleUploadFile={handleUploadFile}
                  myReports={myReports}
                  autocompleteData={autocompleteData}
                />
                <Box
                  display="flex"
                  justifyContent="center"
                  gap={3}
                  alignItems="center"
                  position="sticky"
                  bottom={0}
                  backgroundColor="#fff"
                  zIndex={10}
                >
                  <Box>
                    {selectedRow.id.length === 0 && (
                      <FormButtonPanelUI
                        onCancelClick={() => {
                          setViewField(true);
                          setEditButtonvisible(true);
                          setAddField(true);
                          resetForm();

                          if (mode === EmployeeAttendanceMode.DIALOG) {
                            dialog.handleClose();
                          }
                        }}
                        disabled={editButtonVisible && addField && !EmployeeAttendanceSimpleMode(mode)}
                      />
                    )}
                  </Box>
                  <Box>
                    {EmployeeAttendanceSimpleMode(mode) && <FileUploadButtonUI handleUploadFile={handleUploadFile} />}
                  </Box>
                </Box>
                {selectedRow.id.length > 0 && (
                  <div className="flex justify-center">
                    <ButtonUI
                      className="bg-success"
                      text="rögzítés"
                      onClick={() => {
                        updateBatchedStatuses(values);
                      }}
                    />
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      </div>

      {worksheetLaunch && (
        <DialogUI open fullScreen closeBtn headerContent="Munkalap kitöltése" onClose={() => setWorksheetLaunch(null)}>
          <Worksheet launchContext={worksheetLaunch} onSaved={() => setWorksheetLaunch(null)} />
        </DialogUI>
      )}
      <DeleteDialogUI
        handleRemoveElement={handleRemoveElement}
        show={showDeleteDialog}
        onHide={() => setShowDeleteDialog(false)}
      />
    </>
  );
};
